import type { CountryKey, Image, TrustingCompanies } from "~/types";

interface CountryPageImages {
  hero: Image;
  evisa: Image;
  teaser: Image;
  icon: Image;
  features: Image;
  cta: Image;
}

const EVISA_EXAMPLE_BASE_PATH = '/images/evisa-examples/';
const TRUSTING_COMPANY_IMAGES: Record<TrustingCompanies, Image> = {
  aeroflot: {
    src: '/images/companies/aeroflot.png',
    alt: 'Aeroflot'
  },
  gazprom: {
    src: '/images/companies/gazprom.svg',
    alt: 'Gazprom'
  },
  mts: {
    src: '/images/companies/mts.svg',
    alt: 'MTS'
  },
  sberbank: {
    src: '/images/companies/sberbank.png',
    alt: 'Sberbank'
  },
  yandex: {
    src: '/images/companies/yandex.png',
    alt: 'Yandex'
  },
  tinkoff: {
    src: '/images/companies/tinkoff.png',
    alt: 'Tinkoff'
  },
  atlas_copco: {
    src: '/images/companies/atlas-copco.svg',
    alt: 'Atlas Copco'
  },
  ericsson: {
    src: '/images/companies/ericsson.png',
    alt: 'Ericsson'
  },
  investor_ab: {
    src: '/images/companies/investor-ab.svg',
    alt: 'Investor AB'
  },
  sas: {
    src: '/images/companies/sas.svg',
    alt: 'SAS'
  },
  spotify: {
    src: '/images/companies/spotify.svg',
    alt: 'Spotify'
  },
  swedbank: {
    src: '/images/companies/swedbank.png',
    alt: 'Swedbank'
  },
  arm: {
    src: '/images/companies/arm.svg',
    alt: 'Arm'
  },
  astra_zeneca: {
    src: '/images/companies/astra-zeneca.svg',
    alt: 'Astra Zeneca'
  },
  barclays: {
    src: '/images/companies/barclays.svg',
    alt: 'Barclays'
  },
  british_airways: {
    src: '/images/companies/british-airways.svg',
    alt: 'British Airways'
  },
  revolut: {
    src: '/images/companies/revolut.svg',
    alt: 'Revolut'
  },
  vodafone: {
    src: '/images/companies/vodafone.svg',
    alt: 'Vodafone'
  },
  elal: {
    src: '/images/companies/elal.png',
    alt: 'El Al'
  },
  meta: {
    src: '/images/companies/meta.svg',
    alt: 'Meta'
  },
  nvidia: {
    src: '/images/companies/nvidia.svg',
    alt: 'Nvidia'
  },
  lametayel: {
    src: '/images/companies/lametayel.png',
    alt: 'Lametayel'
  },
  iai: {
    src: '/images/companies/iai.png',
    alt: 'IAI'
  },
  parallel_wireless: {
    src: '/images/companies/parallel-wireless.png',
    alt: 'Parallel Wireless'
  },
  intel: {
    src: '/images/companies/intel.png',
    alt: 'Intel'
  },
  pfizer: {
    src: '/images/companies/pfizer.png',
    alt: 'Pfizer'
  },
  american_airlines: {
    src: '/images/companies/american-airlines.png',
    alt: 'American Airlines'
  },
  airbnb: {
    src: '/images/companies/airbnb.png',
    alt: 'Airbnb'
  },
  tmobile: {
    src: '/images/companies/tmobile.png',
    alt: 'T-Mobile'
  },
  coral_travel: {
    src: 'images/companies/coral-travel.png',
    alt: 'Coral Travel'
  },
  s7_airlines: {
    src: 'images/companies/s7-airlines.svg',
    alt: 'S7 Airlines'
  },
  ving_se: {
    src: 'images/companies/ving-se.svg',
    alt: 'Ving'
  },
  klarna: {
    src: 'images/companies/klarna.svg',
    alt: 'Klarna'
  },
  swedavia: {
    src: 'images/companies/swedavia.svg',
    alt: 'Swedavia'
  },
  ikea: {
    src: 'images/companies/ikea.png',
    alt: 'IKEA'
  }
};
const COUNTRY_IMAGES: Record<CountryKey, CountryPageImages> = {
  india: {
    hero: {
      src: '/images/hero/taj-mahal.jpg',
      alt: 'images.taj_mahal_hero',
      width: 940,
      height: 600
    },
    evisa: {
      src: EVISA_EXAMPLE_BASE_PATH + 'india.webp'
    },
    teaser: {
      src: '/images/pricing/india-taj-mahal.jpg',
      alt: 'images.taj_mahal',
      desc: 'images.taj_mahal_desc'
    },
    icon: {
      src: '/images/pricing/icon-elephant.png',
      alt: 'icons.elephant'
    },
    features: {
      src: '/images/visa-pages/features/india.jpg',
      alt: 'page.product_features.img_india'
    },
    cta: {
      src: '/images/visa-pages/cta/india-buddha-statue.png',
      alt: 'images.buddha_statue_cta'
    }
  },
  canada: {
    hero: {
      src: '/images/hero/toronto.jpg',
      alt: 'images.toronto_hero',
      width: 1024,
      height: 768
    },
    evisa: {
      src: EVISA_EXAMPLE_BASE_PATH + 'canada.jpeg'
    },
    teaser: {
      src: '/images/pricing/canada-niagara-falls.jpg',
      alt: 'images.niagara_falls',
      desc: 'images.niagara_falls_desc'
      },
    icon: {
      src: '/images/pricing/icon-honey.png',
      alt: 'icons.honey'
    },
    features: {
      src: '/images/visa-pages/features/canada.jpg',
      alt: 'page.product_features.img_canada'
    },
    cta: {
      src: '/images/visa-pages/cta/canada-niagara-falls.png',
      alt: 'images.niagara_falls_cta'
    }
  },
  sri_lanka: {
    hero: {
      src: '/images/hero/unawatuna-beach.jpg',
      alt: 'images.unawatuna_beach_hero',
      width: 800,
      height: 500
    },
    evisa: {
      src: EVISA_EXAMPLE_BASE_PATH + 'sri-lanka.jpg'
    },
    teaser: {
      src: '/images/pricing/sri-lanka-arugam-bay.jpg',
      alt: 'images.arugam',
      desc: 'images.arugam_desc'
    },
    icon: {
      src: '/images/pricing/icon-elephant.png',
      alt: 'icons.elephant'
    },
    features: {
      src: '/images/visa-pages/features/sri-lanka.jpg',
      alt: 'page.product_features.img_sri_lanka'
    },
    cta: {
      src: '/images/visa-pages/cta/sri-lanka-elephant.png',
      alt: 'images.elephant_sri_lanka_cta'
    }
  },
  morocco: {
    hero: {
      src: '/images/hero/hassan-ii-mosque.jpg',
      alt: 'images.hassan_ii_mosque_hero',
      width: 1000,
      height: 664
    },
    evisa: {
      src: EVISA_EXAMPLE_BASE_PATH + 'morocco.jpg'
    },
    teaser: {
      src: '/images/pricing/morocco-chefchaouen.jpg',
      alt: 'images.chefchaouen',
      desc: 'images.chefchaouen_desc'
    },
    icon: {
      src: '/images/pricing/icon-morocco.png',
      alt: 'icons.bell'
    },
    features: {
      src: '/images/visa-pages/features/morocco.jpg',
      alt: 'page.product_features.img_morocco'
    },
    cta: {
      src: '/images/visa-pages/cta/morocco-blue-streets-of-chefchaouen.png',
      alt: 'images.chefchaouen_cta'
    }
  },
  new_zealand: {
    hero: {
      src: '/images/hero/new-zealand-landscape.jpg',
      alt: 'images.new_zealand_landscape_hero',
      width: 1000,
      height: 750
    },
    evisa: {
      src: EVISA_EXAMPLE_BASE_PATH + 'new-zealand.jpg'
    },
    teaser: {
      src: '/images/pricing/new-zealand-kokatahi.jpg',
      alt: 'images.kokatahi',
      desc: 'images.kokatahi_desc'
    },
    icon: {
      src: '/images/pricing/icon-new-zealand.png',
      alt: 'icons.meat'
    },
    features: {
      src: '/images/visa-pages/features/new-zealand.jpg',
      alt: 'page.product_features.img_new_zealand'
    },
    cta: {
      src: '/images/visa-pages/cta/new-zealand-mountains-and-fjords.png',
      alt: 'images.new_zealand_landscape_hero'
    }
  },
  vietnam: {
    hero: {
      src: '/images/hero/vietnam-ha-long-bay.jpg',
      alt: 'images.ha_long_bay_cta',
      width: 1000,
      height: 666
    },
    evisa: {
      src: EVISA_EXAMPLE_BASE_PATH + 'vietnam.webp'
    },
    teaser: {
      src: '/images/pricing/vietnam-golden-bridge.jpg',
      alt: 'images.golden_bridge',
      desc: 'images.golden_bridge_desc'
    },
    icon: {
      src: '/images/pricing/icon-coffee.png',
      alt: 'icons.coffee'
    },
    features: {
      src: '/images/visa-pages/features/vietnam.jpg',
      alt: 'page.product_features.img_vietnam'
    },
    cta: {
      src: '/images/visa-pages/cta/vietnam-house-on-beach.jpg',
      alt: 'images.vietnam_house_on_beach_hero'
    }
  },
  usa: {
    hero: {
      src: '/images/hero/manhattan.jpg',
      alt: 'images.manhattan_hero',
      width: 1402,
      height: 935
    },
    evisa: {
      src: EVISA_EXAMPLE_BASE_PATH + 'usa.webp'
    },
    teaser: {
      src: '/images/pricing/usa-grand-canyon.jpg',
      alt: 'images.grand_canyon',
      desc: 'images.grand_canyon_desc'
    },
    icon: {
      src: '/images/pricing/icon-usa.png',
      alt: 'icons.lawn_mower'
    },
    features: {
      src: '/images/visa-pages/features/usa.jpg',
      alt: 'page.product_features.img_usa'
    },
    cta: {
      src: '/images/visa-pages/cta/usa-statue-of-liberty.png',
      alt: 'images.statue_of_liberty_cta'
    }
  },
  korea: {
    hero: {
      src: '/images/hero/gyeongbokgung-palace.jpg',
      alt: 'images.gyeongbokgung_hero',
      width: 1000,
      height: 667
    },
    evisa: {
      src: EVISA_EXAMPLE_BASE_PATH + 'korea.jpeg'
    },
    teaser: {
      src: '/images/pricing/south-korea-jeju-island.jpg',
      alt: 'images.jeju_island',
      desc: 'images.jeju_island_desc'
    },
    icon: {
      src: '/images/pricing/icon-raccoon.png',
      alt: 'icons.raccoon'
    },
    features: {
      src: '/images/visa-pages/features/korea.jpg',
      alt: 'page.product_features.img_korea'
    },
    cta: {
      src: '/images/visa-pages/cta/korea-warrior.png',
      alt: 'images.korean_warrior_cta'
    }
  },
  cambodia: {
    hero: {
      src: '/images/hero/angkor-wat-temple.jpg',
      alt: 'images.angkor_hero',
      width: 900,
      height: 600
    },
    evisa: {
      src: EVISA_EXAMPLE_BASE_PATH + 'cambodia.jpg'
    },
    teaser: {
      src: '/images/pricing/cambodia-angkor-temple.jpg',
      alt: 'images.angkor',
      desc: 'images.angkor_desc'
    },
    icon: {
      src: '/images/pricing/icon-spider.png',
      alt: 'icons.spider'
    },
    features: {
      src: '/images/visa-pages/features/cambodia.jpg',
      alt: 'page.product_features.img_cambodia'
    },
    cta: {
      src: '/images/visa-pages/cta/cambodia-beach.jpg',
      alt: 'images.cambodia_beach_cta'
    }
  },
  israel: {
    hero: {
      src: '/images/hero/dome-of-the-rock.jpg',
      alt: 'images.dome_of_the_rock_hero',
      width: 1000,
      height: 666
    },
    evisa: {
      src: EVISA_EXAMPLE_BASE_PATH + 'israel.jpg'
    },
    teaser: {
      src: '/images/pricing/israel-dead-sea.jpg',
      alt: 'images.dead_sea',
      desc: 'images.dead_sea_desc'
    },
    icon: {
      src: '/images/pricing/icon-building.png',
      alt: 'icons.building'
    },
    features: {
      src: '/images/visa-pages/features/israel.jpg',
      alt: 'page.product_features.img_israel'
    },
    cta: {
      src: '/images/visa-pages/cta/tel-aviv-beach.png',
      alt: 'images.tel_aviv_beach_cta'
    }
  },
  saudi_arabia: {
    hero: {
      src: '/images/hero/masjid-al-haram.jpg',
      alt: 'images.masjid_al_haram_hero',
      width: 1000,
      height: 667
    },
    evisa: {
      src: EVISA_EXAMPLE_BASE_PATH + 'saudi-arabia.webp'
    },
    teaser: {
      src: '/images/pricing/saudi-arabia-madain-saleh.jpg',
      alt: 'images.madain_saleh',
      desc: 'images.madain_saleh_desc'
    },
    icon: {
      src: '/images/pricing/icon-tree.png',
      alt: 'icons.trees'
    },
    features: {
      src: '/images/visa-pages/features/saudi-arabia.jpg',
      alt: 'page.product_features.img_saudi_arabia'
    },
    cta: {
      src: '/images/visa-pages/cta/saudi-arabia-camel.png',
      alt: 'images.camel_saudi_arabia_cta'
    }
  },
  bahrain: {
    hero: {
      src: '/images/hero/al-fateh-grand-mosque.jpg',
      alt: 'images.al_fateh_grand_mosque_hero',
      width: 1600,
      height: 829
    },
    evisa: {
      src: EVISA_EXAMPLE_BASE_PATH + 'bahrain.webp'
    },
    features: {
      src: '/images/visa-pages/features/bahrain.jpg',
      alt: 'page.product_features.img_bahrain'
    },
    teaser: {
      src: '/images/pricing/bahrain-fort.jpg',
      alt: 'images.bahrain_fort',
      desc: 'images.bahrain_fort_desc'
    },
    icon: {
      src: '/images/pricing/icon-shell.png',
      alt: 'icons.clam'
    },
    cta: {
      src: '/images/visa-pages/cta/bahrain-world-trade-center.jpg',
      alt: 'images.bahrain_world_trade_center_cta'
    }
  },
  indonesia: {
    hero: {
      src: '/images/hero/uluwatu-temple.jpg',
      alt: 'images.uluwatu_temple',
      width: 1000,
      height: 666
    },
    evisa: {
      src: EVISA_EXAMPLE_BASE_PATH + 'indonesia.webp'
    },
    teaser: {
      src: '/images/pricing/indonesia-borobudur.jpg',
      alt: 'images.borobudur',
      desc: 'images.borobudur_desc'
    },
    icon: {
      src: '/images/pricing/icon-indonesia.png',
      alt: 'icons.volcano'
    },
    features: {
      src: '/images/visa-pages/features/indonesia.jpg',
      alt: 'page.product_features.img_indonesia'
    },
    cta: {
      src: '/images/visa-pages/cta/indonesia-balinese-dancer.png',
      alt: 'images.balinese_dancer_cta'
    }
  },
  uk: {
    hero: {
      src: '/images/hero/big-ben.jpg',
      alt: 'images.big_ben',
      width: 1280,
      height: 851
    },
    icon: {
      src: '/images/pricing/icon-elephant.png',
      alt: 'icons.elephant'
    },
    cta: {
      src: '/images/visa-pages/cta/british-guard.png',
      alt: 'images.british_guard_cta'
    },
    evisa: {
      src: EVISA_EXAMPLE_BASE_PATH + 'uk.jpg'
    },
    teaser: {
      src: '/images/pricing/uk-edinburgh-castle.jpg',
      alt: 'images.edinburgh_castle',
      desc: 'images.edinburgh_castle_desc'
    },
    features: {
      src: '/images/visa-pages/features/uk.jpg',
      alt: 'page.product_features.img_uk'
    },
  },
  togo: {
    hero: {
      src: '/images/hero/togo-village.jpg',
      alt: 'images.togo_village_hero',
      width: 660,
      height: 428
    },
    icon: {
      src: '/images/pricing/icon-tree.png',
      alt: 'icons.trees'
    },
    cta: {
      src: '/images/visa-pages/cta/togo-waterfall.jpg',
      alt: 'images.togo_waterfall'
    },
    evisa: {
      src: EVISA_EXAMPLE_BASE_PATH + 'togo.webp'
    },
    teaser: {
      src: '/images/pricing/togo-lush.jpg',
      alt: 'images.togo_waterfall',
      desc: 'images.togo_waterfall_desc'
    },
    features: {
      src: '/images/visa-pages/features/togo-buddha.jpg',
      alt: 'page.product_features.img_togo'
    },
  },
  thailand: {
    hero: {
      src: '/images/hero/phi-phi-islands.jpg',
      alt: 'images.phi_phi_islands',
      width: 540,
      height: 540
    },
    icon: {
      src: '/images/pricing/icon-tree.png',
      alt: 'icons.trees'
    },
    cta: {
      src: '/images/visa-pages/cta/wat-chalong-temple.jpg',
      alt: 'images.wat_chalong'
    },
    evisa: {
      src: EVISA_EXAMPLE_BASE_PATH + 'thailand.webp'
    },
    teaser: {
      src: '/images/pricing/phi-phi-islands.jpg',
      alt: 'images.phi_phi_islands',
      desc: 'images.phi_phi_islands_desc'
    },
    features: {
      src: '/images/visa-pages/features/phuket.webp',
      alt: 'page.product_features.img_thailand'
    }
  },
  schengen: {
    hero: {
      src: '/images/hero/eiffel.jpg',
      alt: 'images.eiffel_tower',
      width: 1000,
      height: 667
    },
    icon: {
      src: '/images/pricing/icon-coffee.png',
      alt: 'icons.coffee'
    },
    cta: {
      src: '/images/visa-pages/cta/notre-dame.jpg',
      alt: 'images.notre_dame'
    },
    evisa: {
      src: EVISA_EXAMPLE_BASE_PATH + 'schengen.jpg'
    },
    teaser: {
      src: '/images/pricing/louvre.jpg',
      alt: 'images.louvre',
      desc: 'images.louvre_desc'
    },
    features: {
      src: '/images/pricing/louvre.jpg',
      alt: 'images.louvre',
    }
  }
};

export function getTrustingCompanyImage(company: TrustingCompanies) {
  return TRUSTING_COMPANY_IMAGES[company];
}

export function getCountryImages(country: CountryKey) {
  return COUNTRY_IMAGES[country];
}